import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PersonaConfig } from '../types';
import { TauriAPI } from '../lib/tauri';
import { 
  Scale, 
  DollarSign, 
  Building, 
  Code, 
  Users, 
  Briefcase,
  ArrowRight 
} from 'lucide-react';

interface PersonaSelectorProps {
  onPersonaSelected: (persona: PersonaConfig) => void;
}

export const PersonaSelector: React.FC<PersonaSelectorProps> = ({
  onPersonaSelected,
}) => {
  const [personas, setPersonas] = useState<PersonaConfig[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<PersonaConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPersonas = async () => {
      try {
        const personaConfigs = await TauriAPI.getPersonaConfigs();
        setPersonas(personaConfigs);
      } catch (error) {
        console.error('Failed to load persona configurations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPersonas();
  }, []);

  const getPersonaIcon = (personaId: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      'legal_professional': Scale,
      'business_owner': Briefcase,
      'property_manager': Building,
      'developer': Code,
      'financial_manager': DollarSign,
      'team_leader': Users,
    };
    return iconMap[personaId] || Briefcase;
  };

  const handlePersonaSelect = (persona: PersonaConfig) => {
    setSelectedPersona(persona);
  };

  const handleContinue = () => {
    if (selectedPersona) {
      onPersonaSelected(selectedPersona);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading persona configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4">Choose Your Persona</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Select the persona that best describes your primary role. This will customize 
          ChittyOS with the most relevant modules, integrations, and workflows for your needs.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {personas.map((persona) => {
          const IconComponent = getPersonaIcon(persona.id);
          const isSelected = selectedPersona?.id === persona.id;

          return (
            <Card
              key={persona.id}
              className={`cursor-pointer transition-all hover:shadow-lg ${
                isSelected ? 'ring-2 ring-primary border-primary' : ''
              }`}
              onClick={() => handlePersonaSelect(persona)}
            >
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-3 p-3 rounded-full bg-primary/10">
                  <IconComponent className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-xl">{persona.name}</CardTitle>
                <CardDescription className="text-sm">
                  {persona.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2">Primary Services</h4>
                  <div className="flex flex-wrap gap-1">
                    {persona.primary_services.slice(0, 3).map((service) => (
                      <Badge key={service} variant="secondary" className="text-xs">
                        {service}
                      </Badge>
                    ))}
                    {persona.primary_services.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{persona.primary_services.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-2">Modules Included</h4>
                  <div className="flex flex-wrap gap-1">
                    {persona.recommended_modules.slice(0, 2).map((module) => (
                      <Badge key={module} variant="outline" className="text-xs">
                        {module}
                      </Badge>
                    ))}
                    {persona.recommended_modules.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{persona.recommended_modules.length - 2} more
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedPersona && (
        <div className="max-w-2xl mx-auto">
          <Card className="border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  {React.createElement(getPersonaIcon(selectedPersona.id), {
                    className: "w-6 h-6 text-primary"
                  })}
                </div>
                {selectedPersona.name} Configuration
              </CardTitle>
              <CardDescription>
                Review what will be configured for your selected persona
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Services ({selectedPersona.primary_services.length})</h4>
                  <div className="space-y-2">
                    {selectedPersona.primary_services.map((service) => (
                      <div key={service} className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Modules ({selectedPersona.recommended_modules.length})</h4>
                  <div className="space-y-2">
                    {selectedPersona.recommended_modules.map((module) => (
                      <div key={module} className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                        <span>{module}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-center pt-4">
                <Button onClick={handleContinue} size="lg" className="gap-2">
                  Continue with {selectedPersona.name}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};